import { useRef } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input, SubmitButton } from 'formik-antd';
import * as Yup from 'yup';

import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { createCustomerGroup } from 'redux/reducers/clientSlice';

import { CustomerGroupOption } from '../../CustomerGroupForm';

import styles from './AddCustomerGroupForm.module.less';

const requiredMessage = 'Field is required';

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required(requiredMessage)
    .max(120, 'Group name should be 120 characters maximum'),
});

interface CustomerGroupFormValues {
  name: string;
}

type Props = {
  loading: boolean;
  customerGroupOptions: CustomerGroupOption[];
  onLoadingChange: (value: boolean) => void;
  onSubmit: () => void;
};

export default function AddCustomerGroupForm(props: Props) {
  const { loading, customerGroupOptions, onLoadingChange, onSubmit } = props;

  const formRef: any = useRef(null);

  const dispatch = useAppDispatch();

  const initialValues: CustomerGroupFormValues = {
    name: '',
  };

  const handleSubmit = async (values: CustomerGroupFormValues) => {
    const trimmedName = values.name.trim();

    if (
      customerGroupOptions.length &&
      customerGroupOptions.some((customerGroupOption) => {
        const { label = '' } = customerGroupOption;

        return label.toLowerCase() === trimmedName.toLowerCase();
      })
    ) {
      formRef.current.setFieldError(
        'name',
        'A group with this name already exists',
      );

      return;
    }

    const data = {
      name: trimmedName,
    };

    onLoadingChange(true);

    const result = await dispatch(createCustomerGroup(data));

    if (createCustomerGroup.fulfilled.match(result)) {
      onSubmit();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      onLoadingChange(false);
    }
  };

  return (
    <Formik
      innerRef={formRef}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      <Form>
        <FormItem name="name">
          <Input
            name="name"
            placeholder="Enter customer group name"
            autoComplete="off"
          />
        </FormItem>

        <div className={styles.submitBox}>
          <SubmitButton block loading={loading}>
            Create
          </SubmitButton>
        </div>
      </Form>
    </Formik>
  );
}
